import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  fetchAccountInfo,
  extractAccountCookies,
  updateAccountCookiePreferences,
  type AccountCookiePreference,
} from '../../features/accountInfo';
import {
  accountCookiesToConsent,
  consentToAccountCookies,
  getEffectiveConsent,
  isGpcEnabled,
  persistConsent,
  OPEN_COOKIE_PREFERENCES_EVENT,
  type ConsentCategories,
  type OpenCookiePreferencesDetail,
} from '../../features/cookieConsent';

type CookieConsentProps = {
  // Wire this to analytics gating (Google Consent Mode v2 + Clarity).
  onConsentChange: (consent: ConsentCategories) => void;
};

// Split the banner body on {{cookiePolicy}} / {{privacyPolicy}} and interleave
// underlined router links, matching the footer's link styling.
function BannerBody({ body }: { body: string }) {
  const { t } = useTranslation();
  const parts = body.split(/(\{\{cookiePolicy\}\}|\{\{privacyPolicy\}\})/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part === '{{cookiePolicy}}') {
          return (
            <Link key={index} to="/cookie-policy" className="underline underline-offset-2 text-ink-900 hover:text-ink-700">
              {t('cookie.link.cookiePolicy')}
            </Link>
          );
        }
        if (part === '{{privacyPolicy}}') {
          return (
            <Link key={index} to="/privacy" className="underline underline-offset-2 text-ink-900 hover:text-ink-700">
              {t('cookie.link.privacyPolicy')}
            </Link>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

function sameCookiePreferences(
  left: AccountCookiePreference[] | null | undefined,
  right: AccountCookiePreference[] | null | undefined,
) {
  if (left === right) {
    return true;
  }
  if (!left || !right || left.length !== right.length) {
    return false;
  }

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
  toggleRef,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  label: string;
  toggleRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={toggleRef}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={[
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-200 motion-reduce:transition-none',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-2',
        checked ? 'border-ink-900 bg-ink-900' : 'border-line-400 bg-line-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition-transform duration-200 motion-reduce:transition-none',
          checked ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}

export default function CookieConsent({ onConsentChange }: CookieConsentProps) {
  const { t } = useTranslation();
  const titleId = useId();

  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showGpcNotice, setShowGpcNotice] = useState(false);
  const [focusAnalytics, setFocusAnalytics] = useState(false);
  const [isResolvingAccountConsent, setIsResolvingAccountConsent] = useState(true);

  // Draft toggle state inside the modal (essential is always on).
  const [analytics, setAnalytics] = useState(true);
  const [functional, setFunctional] = useState(true);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const analyticsToggleRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const accountCookiesRef = useRef<AccountCookiePreference[] | null | undefined>(undefined);

  useEffect(() => {
    let isActive = true;

    const effective = getEffectiveConsent();
    setAnalytics(effective.analytics);
    setFunctional(effective.functional);

    void (async () => {
      try {
        const accountInfo = await fetchAccountInfo();
        if (!isActive) {
          return;
        }

        const accountCookies = extractAccountCookies(accountInfo);
        accountCookiesRef.current = accountCookies;

        const serverConsent = accountCookiesToConsent(accountCookies);
        if (serverConsent) {
          const effectiveServerConsent = isGpcEnabled()
            ? { ...serverConsent, analytics: false }
            : serverConsent;
          persistConsent(effectiveServerConsent);
          onConsentChange(effectiveServerConsent);
          setAnalytics(effectiveServerConsent.analytics);
          setFunctional(effectiveServerConsent.functional);
          setShowBanner(false);
          setShowGpcNotice(false);
          return;
        }

        if (accountCookies === null) {
          setShowBanner(true);
          if (isGpcEnabled()) {
            setShowGpcNotice(true);
          }
          return;
        }

        setShowBanner(false);
        setShowGpcNotice(false);
      } catch {
        if (!isActive) {
          return;
        }
        setShowBanner(false);
        setShowGpcNotice(false);
      } finally {
        if (isActive) {
          setIsResolvingAccountConsent(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [onConsentChange]);

  const commit = useCallback(
    async (categories: ConsentCategories) => {
      const nextCookies = consentToAccountCookies(categories);
      if (!sameCookiePreferences(accountCookiesRef.current, nextCookies)) {
        await updateAccountCookiePreferences(nextCookies);
        accountCookiesRef.current = nextCookies;
      }
      persistConsent(categories);
      onConsentChange(categories);
      setAnalytics(categories.analytics);
      setFunctional(categories.functional);
      setShowBanner(false);
      setShowModal(false);
      setShowGpcNotice(false);
    },
    [onConsentChange],
  );

  const acceptAll = useCallback(() => {
    void commit({ analytics: true, functional: true });
  }, [commit]);
  const rejectOptional = useCallback(() => {
    void commit({ analytics: false, functional: false });
  }, [commit]);
  const savePreferences = useCallback(() => {
    void commit({ analytics, functional });
  }, [analytics, functional, commit]);

  const openModal = useCallback((focus?: boolean) => {
    // Re-seed drafts from the current effective consent each time the modal opens.
    const effective = getEffectiveConsent();
    setAnalytics(effective.analytics);
    setFunctional(effective.functional);
    setFocusAnalytics(Boolean(focus));
    setShowModal(true);
  }, []);

  // Footer links / in-policy links open the modal via a window event.
  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenCookiePreferencesDetail>).detail;
      openModal(detail?.focus === 'analytics');
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
  }, [openModal]);

  // Modal a11y: focus management, focus trap, Esc to close.
  useEffect(() => {
    if (!showModal) {
      return;
    }

    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    const focusTimer = window.setTimeout(() => {
      if (focusAnalytics && analyticsToggleRef.current) {
        analyticsToggleRef.current.focus();
      } else {
        const first = dialogRef.current?.querySelector<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        );
        first?.focus();
      }
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setShowModal(false);
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) {
        return;
      }
      const list = Array.from(focusable);
      const first = list[0];
      const last = list[list.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown, true);
      lastFocusedRef.current?.focus?.();
    };
  }, [showModal, focusAnalytics]);

  if (isResolvingAccountConsent || (!showBanner && !showModal)) {
    return null;
  }

  const primaryButton =
    'inline-flex items-center justify-center rounded-button bg-ink-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-ink-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-2 motion-reduce:transition-none';
  const secondaryButton =
    'inline-flex items-center justify-center rounded-button border border-line-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-ink-900 transition-colors duration-200 hover:border-ink-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-2 motion-reduce:transition-none';

  return (
    <div data-analytics-ignore="true">
      {/* ── Banner ── */}
      {showBanner && !showModal && (
        <div
          role="region"
          aria-label={t('cookie.modal.title')}
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-line-200 bg-white/95 backdrop-blur-xl text-ink-900 shadow-[0_-8px_30px_rgba(0,0,0,0.06)]"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-4 py-5 lg:flex-row lg:items-center lg:justify-between lg:px-6">
            <div className="max-w-3xl">
              {showGpcNotice && (
                <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-700">
                  {t('cookie.gpc.notice')}
                </p>
              )}
              <p className="text-[13px] leading-6 text-ink-500">
                <BannerBody body={t('cookie.banner.body')} />
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:shrink-0">
              <button type="button" className={secondaryButton} onClick={() => openModal(false)}>
                {t('cookie.banner.manageSettings')}
              </button>
              <button type="button" className={secondaryButton} onClick={rejectOptional}>
                {t('cookie.banner.rejectOptional')}
              </button>
              <button type="button" className={primaryButton} onClick={acceptAll}>
                {t('cookie.banner.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preferences modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center">
          <div
            className="absolute inset-0 bg-ink-900/40 backdrop-blur-xs"
            onClick={() => setShowModal(false)}
            aria-hidden="true"
          />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative z-[71] flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden border border-line-200 bg-white shadow-xl sm:max-h-[85vh]"
          >
            <div className="flex items-start justify-between gap-4 border-b border-line-200 px-6 py-5">
              <div>
                <h2 id={titleId} className="font-serif text-h4 text-ink-900">
                  {t('cookie.modal.title')}
                </h2>
                <p className="mt-1 text-[13px] leading-6 text-ink-500">{t('cookie.modal.intro')}</p>
              </div>
              <button
                type="button"
                aria-label={t('cookie.modal.close')}
                onClick={() => setShowModal(false)}
                className="-mr-1 flex h-8 w-8 items-center justify-center text-ink-400 transition-colors hover:text-ink-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ink-900"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {showGpcNotice && (
                <p className="mb-4 border border-line-200 bg-cream-50 px-3 py-2 text-[12px] leading-5 text-ink-700">
                  {t('cookie.gpc.notice')}
                </p>
              )}

              <ul className="space-y-5">
                {/* Strictly necessary — locked on */}
                <li className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[14px] font-semibold text-ink-900">{t('cookie.category.essential.name')}</p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-500">{t('cookie.category.essential.desc')}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Toggle checked disabled label={t('cookie.category.essential.name')} />
                    <span className="text-[11px] uppercase tracking-[0.12em] text-ink-400">
                      {t('cookie.category.essential.always')}
                    </span>
                  </div>
                </li>

                {/* Performance & analytics */}
                <li className="flex items-start justify-between gap-4 border-t border-line-200 pt-5">
                  <div>
                    <p className="text-[14px] font-semibold text-ink-900">{t('cookie.category.analytics.name')}</p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-500">{t('cookie.category.analytics.desc')}</p>
                  </div>
                  <Toggle
                    toggleRef={analyticsToggleRef}
                    checked={analytics}
                    onChange={setAnalytics}
                    label={t('cookie.category.analytics.name')}
                  />
                </li>

                {/* Functional */}
                <li className="flex items-start justify-between gap-4 border-t border-line-200 pt-5">
                  <div>
                    <p className="text-[14px] font-semibold text-ink-900">{t('cookie.category.functional.name')}</p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-500">{t('cookie.category.functional.desc')}</p>
                  </div>
                  <Toggle checked={functional} onChange={setFunctional} label={t('cookie.category.functional.name')} />
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-3 border-t border-line-200 px-6 py-5 sm:flex-row sm:justify-end">
              <button type="button" className={secondaryButton} onClick={rejectOptional}>
                {t('cookie.modal.rejectOptional')}
              </button>
              <button type="button" className={secondaryButton} onClick={acceptAll}>
                {t('cookie.modal.acceptAll')}
              </button>
              <button type="button" className={primaryButton} onClick={savePreferences}>
                {t('cookie.modal.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
